'use client';

import { logger } from '@adornis/base/logging.js';
import {
  ArrowLeft,
  BookOpen,
  Check,
  ChevronRight,
  Clock,
  Copy,
  GraduationCap,
  Inbox,
  Link as LinkIcon,
  MoreVertical,
  Plus,
  QrCode,
  RefreshCw,
  School,
  Users,
  X,
} from 'lucide-react';
import type { ComponentType } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'react-qr-code';
import { createClassroomMutation } from '../../../db/api/create-classroom.js';
import { decideJoinRequestMutation } from '../../../db/api/decide-join-request.js';
import { getApprovedMemberCountsQuery } from '../../../db/api/get-approved-member-counts.js';
import { getClassExamOverviewQuery, type ClassExamSummaryDTO } from '../../../db/api/get-class-exam-overview.js';
import { getMyClassesQuery } from '../../../db/api/get-my-classes.js';
import { getPendingRequestCountsQuery } from '../../../db/api/get-pending-request-counts.js';
import { getRosterForClassQuery } from '../../../db/api/get-roster-for-class.js';
import { getStudentsByIdsQuery } from '../../../db/api/get-students-by-ids.js';
import { regenerateClassroomCodeMutation } from '../../../db/api/regenerate-classroom-code.js';
import type { ClassMembership } from '../../../db/class-membership.js';
import type { ClassRoom } from '../../../db/classroom.js';
import type { Student } from '../../../db/student.js';
import { isSubject } from '../../../db/subjects.js';
import { Badge } from '../ui/badge.js';
import { Button } from '../ui/button.js';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog.js';
import { Input } from '../ui/input.js';

interface ClassroomPageProps {
  onBack: () => void;
  onOpenExam: (conversationId: string) => void;
}

/** Rotating avatar background, chosen from a student's `_id` so the same student always gets the same color. */
const AVATAR_COLORS = ['bg-brand-400', 'bg-brand-600', 'bg-brand-800', 'bg-slate-500', 'bg-slate-700'] as const;

function colorForKey(key: string, palette: readonly string[]): string {
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  return palette[hash % palette.length] ?? palette[0]!;
}

function getInitials(firstName?: string, lastName?: string): string {
  const initials = `${firstName?.trim()?.[0] ?? ''}${lastName?.trim()?.[0] ?? ''}`.toUpperCase();
  return initials || '?';
}

interface EmptyStateProps {
  icon: ComponentType<{ className?: string }>;
  title: string;
  hint?: string;
}

function EmptyState({ icon: Icon, title, hint }: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center justify-center text-center gap-2 py-10 px-4">
      <div className="w-12 h-12 rounded-full bg-brand-surface flex items-center justify-center">
        <Icon className="h-5 w-5 text-brand-text" />
      </div>
      <p className="text-sm font-medium text-foreground">{title}</p>
      {hint && <p className="text-xs text-muted-foreground max-w-[16rem]">{hint}</p>}
    </div>
  );
}

export function ClassroomPage({ onBack, onOpenExam }: ClassroomPageProps) {
  const { t } = useTranslation();

  const [classrooms, setClassrooms] = useState<ClassRoom[]>([]);
  const [pendingCounts, setPendingCounts] = useState<Record<string, number>>({});
  const [studentCounts, setStudentCounts] = useState<Record<string, number>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newClassName, setNewClassName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [copiedID, setCopiedID] = useState<string | null>(null);
  const [selectedClassRoomID, setSelectedClassRoomID] = useState<string | null>(null);

  const loadClasses = () => {
    setIsLoading(true);
    void Promise.all([
      getMyClassesQuery()({ _id: 1, name: 1, code: 1, createdAt: 1 }),
      getPendingRequestCountsQuery()({ _id: 1, classRoomID: 1 }),
      getApprovedMemberCountsQuery()({ _id: 1, classRoomID: 1 }),
    ])
      .then(([classes, pending, approved]) => {
        setClassrooms(classes ?? []);

        const pendingByClass: Record<string, number> = {};
        for (const membership of pending ?? []) {
          pendingByClass[membership.classRoomID] = (pendingByClass[membership.classRoomID] ?? 0) + 1;
        }
        setPendingCounts(pendingByClass);

        const studentsByClass: Record<string, number> = {};
        for (const membership of approved ?? []) {
          studentsByClass[membership.classRoomID] = (studentsByClass[membership.classRoomID] ?? 0) + 1;
        }
        setStudentCounts(studentsByClass);
      })
      .catch(err => logger.error(err, 'Failed to load classes'))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    loadClasses();
  }, []);

  const handleCreate = async () => {
    const trimmed = newClassName.trim();
    if (!trimmed) return;

    setIsCreating(true);
    try {
      await createClassroomMutation(trimmed)({ _id: 1 });
      setNewClassName('');
      setIsCreateOpen(false);
      loadClasses();
    } catch (err) {
      logger.error(err, 'Failed to create class');
      alert(t('Classroom.alerts.createError'));
    } finally {
      setIsCreating(false);
    }
  };

  const handleCopy = async (id: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedID(id);
      setTimeout(() => setCopiedID(prev => (prev === id ? null : prev)), 2000);
    } catch (err) {
      logger.error(err, 'Failed to copy to clipboard');
    }
  };

  const selectedClassroom = classrooms.find(c => c._id === selectedClassRoomID) ?? null;

  if (selectedClassroom) {
    return (
      <ClassRoster
        classroom={selectedClassroom}
        onBack={() => {
          setSelectedClassRoomID(null);
          loadClasses();
        }}
        onCodeRegenerated={updated => {
          setClassrooms(prev => prev.map(c => (c._id === updated._id ? updated : c)));
        }}
        onOpenExam={onOpenExam}
      />
    );
  }

  return (
    <div className="flex flex-col h-full w-full bg-background overflow-y-auto">
      <div className="sticky top-0 z-10 border-b border-border bg-card px-4 sm:px-8 py-2 sm:py-4 shadow-sm">
        <div className="flex items-center justify-between gap-2 max-w-6xl mx-auto">
          <div className="flex items-center gap-2">
            <Button
              onClick={onBack}
              variant="ghost"
              size="icon"
              className="text-muted-foreground hover:text-foreground hover:bg-accent"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-xl sm:text-xl font-bold text-foreground tracking-tight">{t('Classroom.title')}</h1>
          </div>
          <Button onClick={() => setIsCreateOpen(true)} size="sm" className="gap-1.5">
            <Plus className="h-4 w-4" /> {t('Classroom.createButton')}
          </Button>
        </div>
      </div>

      <div className="flex-1 px-4 sm:px-8 py-8">
        <div className="max-w-6xl mx-auto animate-in fade-in duration-500">
          {isLoading ? (
            <p className="text-sm text-muted-foreground">{t('Classroom.loading')}</p>
          ) : classrooms.length === 0 ? (
            <EmptyState icon={School} title={t('Classroom.noClasses')} hint={t('Classroom.noClassesHint')} />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {classrooms.map(classroom => {
                const pendingCount = pendingCounts[classroom._id] ?? 0;
                const studentCount = studentCounts[classroom._id] ?? 0;
                const inviteLink = `${window.location.origin}/join?code=${classroom.code}`;
                const codeCopyID = `${classroom._id}-code`;
                const linkCopyID = `${classroom._id}-link`;

                return (
                  <div
                    key={classroom._id}
                    className="relative flex flex-col bg-card border border-border rounded-xl overflow-hidden hover:shadow-md transition-shadow"
                  >
                    {pendingCount > 0 && (
                      <span
                        className="absolute top-3.5 right-3.5 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-card"
                        aria-hidden="true"
                      />
                    )}

                    <button
                      type="button"
                      onClick={() => setSelectedClassRoomID(classroom._id)}
                      className="text-left p-4 sm:p-5 flex-1"
                    >
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-9 h-9 rounded-lg bg-brand-surface flex items-center justify-center shrink-0">
                          <School className="h-4 w-4 text-brand-text" />
                        </div>
                        <p className="font-semibold text-foreground truncate min-w-0">{classroom.name}</p>
                      </div>

                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Users className="h-3.5 w-3.5" /> {t('Classroom.studentCount', { count: studentCount })}
                        </span>
                        {pendingCount > 0 && (
                          <Badge className="bg-amber-500">
                            {t('Classroom.pendingCount', { count: pendingCount })}
                          </Badge>
                        )}
                      </div>

                      <p className="text-xs text-muted-foreground mt-3 font-mono tracking-widest">{classroom.code}</p>
                    </button>

                    <div className="flex items-center gap-1.5 px-4 sm:px-5 pb-4">
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1.5 flex-1"
                        onClick={() => handleCopy(codeCopyID, classroom.code)}
                      >
                        {copiedID === codeCopyID ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                        {t('Classroom.copyCode')}
                      </Button>
                      <Button
                        variant="outline"
                        size="icon-sm"
                        onClick={() => handleCopy(linkCopyID, inviteLink)}
                        aria-label={t('Classroom.copyLink')}
                      >
                        {copiedID === linkCopyID ? <Check className="h-3.5 w-3.5" /> : <LinkIcon className="h-3.5 w-3.5" />}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="sm:max-w-[425px] bg-card text-card-foreground border-border">
          <DialogHeader>
            <DialogTitle>{t('Classroom.createDialog.title')}</DialogTitle>
          </DialogHeader>
          <Input
            value={newClassName}
            onChange={e => setNewClassName(e.target.value)}
            placeholder={t('Classroom.createDialog.namePlaceholder')}
            onKeyDown={e => {
              if (e.key === 'Enter') void handleCreate();
            }}
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setIsCreateOpen(false)}>
              {t('Classroom.createDialog.cancel')}
            </Button>
            <Button onClick={() => void handleCreate()} disabled={isCreating || !newClassName.trim()}>
              {t('Classroom.createDialog.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface ClassRosterProps {
  classroom: ClassRoom;
  onBack: () => void;
  onCodeRegenerated: (updated: ClassRoom) => void;
  onOpenExam: (conversationId: string) => void;
}

/** An exam has graded results once at least one submission has been graded. */
const isGraded = (status: string | null | undefined) => status === 'graded';

function ClassRoster({ classroom, onBack, onCodeRegenerated, onOpenExam }: ClassRosterProps) {
  const { t, i18n } = useTranslation();

  const [current, setCurrent] = useState(classroom);
  const [pending, setPending] = useState<ClassMembership[]>([]);
  const [approved, setApproved] = useState<ClassMembership[]>([]);
  const [studentsByID, setStudentsByID] = useState<Record<string, Student>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [decidingID, setDecidingID] = useState<string | null>(null);
  const [isQrOpen, setIsQrOpen] = useState(false);
  const [openMenuID, setOpenMenuID] = useState<string | null>(null);

  const [exams, setExams] = useState<ClassExamSummaryDTO[]>([]);
  const [isLoadingExams, setIsLoadingExams] = useState(false);

  const loadRoster = () => {
    setIsLoading(true);
    void Promise.all([
      getRosterForClassQuery(current._id, 'pending')({ _id: 1, studentID: 1, requestedAt: 1 }),
      getRosterForClassQuery(current._id, 'approved')({ _id: 1, studentID: 1, requestedAt: 1, decidedAt: 1 }),
    ])
      .then(([pendingRows, approvedRows]) => {
        setPending(pendingRows ?? []);
        setApproved(approvedRows ?? []);

        const studentIDs = Array.from(
          new Set([...(pendingRows ?? []), ...(approvedRows ?? [])].map(m => m.studentID)),
        );
        if (studentIDs.length === 0) return;

        return getStudentsByIdsQuery(studentIDs)({ _id: 1, firstName: 1, lastName: 1, email: 1 }).then(students => {
          const map: Record<string, Student> = {};
          for (const student of students ?? []) map[student._id] = student;
          setStudentsByID(map);
        });
      })
      .catch(err => logger.error(err, 'Failed to load class roster'))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    loadRoster();
  }, [current._id]);

  useEffect(() => {
    let isMounted = true;
    setIsLoadingExams(true);

    void getClassExamOverviewQuery(current.name)({
      conversationId: 1,
      title: 1,
      subject: 1,
      status: 1,
      createdAt: 1,
      submissionCount: 1,
      averagePercentage: 1,
      averageGrade: 1,
    })
      .then(rows => {
        if (isMounted) setExams(rows ?? []);
      })
      .catch(err => logger.error(err, 'Failed to load class exams'))
      .finally(() => {
        if (isMounted) setIsLoadingExams(false);
      });

    return () => {
      isMounted = false;
    };
  }, [current.name]);

  const subjectCount = useMemo(() => new Set(exams.map(e => e.subject).filter(Boolean)).size, [exams]);
  const gradedExams = useMemo(() => exams.filter(e => e.submissionCount > 0), [exams]);
  const classAverageGrade = useMemo(() => {
    const grades = gradedExams.map(e => e.averageGrade).filter((g): g is number => g != null);
    if (grades.length === 0) return null;
    return Math.round((grades.reduce((sum, g) => sum + g, 0) / grades.length) * 10) / 10;
  }, [gradedExams]);

  const subjectLabel = (subject?: string | null) =>
    subject && isSubject(subject) ? t(`Subjects.${subject}`) : subject || t('ClassInsights.unknownSubject');

  const formatDate = (value?: string | null) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat(i18n.language, { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date);
  };

  const handleDecide = async (membershipID: string, approve: boolean) => {
    setDecidingID(membershipID);
    try {
      await decideJoinRequestMutation(membershipID, approve)();
      loadRoster();
    } catch (err) {
      logger.error(err, 'Failed to decide join request');
      alert(t('Classroom.alerts.decideError'));
    } finally {
      setDecidingID(null);
    }
  };

  const handleRegenerate = async () => {
    setIsRegenerating(true);
    try {
      const updated = await regenerateClassroomCodeMutation(current._id)({ _id: 1, name: 1, code: 1, createdAt: 1 });
      if (updated) {
        setCurrent(updated as unknown as ClassRoom);
        onCodeRegenerated(updated as unknown as ClassRoom);
      }
    } catch (err) {
      logger.error(err, 'Failed to regenerate code');
      alert(t('Classroom.alerts.regenerateError'));
    } finally {
      setIsRegenerating(false);
    }
  };

  const studentLabel = (studentID: string) => {
    const student = studentsByID[studentID];
    return student ? `${student.firstName} ${student.lastName}`.trim() : studentID;
  };

  const inviteLink = `${typeof window !== 'undefined' ? window.location.origin : ''}/join?code=${current.code}`;

  return (
    <div className="flex flex-col h-full w-full bg-background overflow-y-auto">
      <div className="sticky top-0 z-10 border-b border-border bg-card px-4 sm:px-8 py-2 sm:py-4 shadow-sm">
        <div className="flex items-center gap-2 max-w-6xl mx-auto">
          <Button
            onClick={onBack}
            variant="ghost"
            size="icon"
            className="text-muted-foreground hover:text-foreground hover:bg-accent"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-foreground tracking-tight">{current.name}</h1>
            {!isLoading && (
              <p className="text-xs text-muted-foreground">{t('Classroom.studentCount', { count: approved.length })}</p>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 px-4 sm:px-8 py-8">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6 animate-in fade-in duration-500">
          {/* Sidebar: join code + pending requests — narrower column, shown first on mobile */}
          <div className="order-1 lg:order-2 lg:col-span-1 flex flex-col gap-6">
            <section className="bg-card rounded-xl p-5 border border-border shadow-sm">
              <p className="text-xs text-muted-foreground mb-1">{t('Classroom.joinCodeLabel')}</p>
              <p className="text-xl font-mono tracking-widest text-foreground mb-3">{current.code}</p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" className="gap-1.5 flex-1" onClick={() => setIsQrOpen(true)}>
                  <QrCode className="h-3.5 w-3.5" /> {t('Classroom.showQrCode')}
                </Button>
                <Button
                  variant="outline"
                  size="icon-sm"
                  onClick={() => void handleRegenerate()}
                  disabled={isRegenerating}
                  aria-label={t('Classroom.regenerateCode')}
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                </Button>
              </div>
            </section>

            <section className="bg-card rounded-xl p-5 border border-border shadow-sm">
              <h2 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                {t('Classroom.pendingRequestsTitle')}
                {pending.length > 0 && <Badge>{pending.length}</Badge>}
              </h2>

              {isLoading ? (
                <p className="text-sm text-muted-foreground">{t('Classroom.loading')}</p>
              ) : pending.length === 0 ? (
                <EmptyState
                  icon={Inbox}
                  title={t('Classroom.noPendingRequests')}
                  hint={t('Classroom.noPendingRequestsHint')}
                />
              ) : (
                <div className="space-y-2">
                  {pending.map(membership => (
                    <div
                      key={membership._id}
                      className="flex items-center justify-between gap-2 p-3 bg-background border border-border rounded-lg"
                    >
                      <p className="font-medium text-foreground truncate text-sm">
                        {studentLabel(membership.studentID)}
                      </p>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          size="icon-sm"
                          variant="outline"
                          className="text-destructive hover:text-destructive"
                          aria-label={t('Classroom.reject')}
                          onClick={() => void handleDecide(membership._id, false)}
                          disabled={decidingID === membership._id}
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="icon-sm"
                          aria-label={t('Classroom.approve')}
                          onClick={() => void handleDecide(membership._id, true)}
                          disabled={decidingID === membership._id}
                        >
                          <Check className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>

          {/* Main column: roster — wider, shown second on mobile */}
          <section className="order-2 lg:order-1 lg:col-span-2 bg-card rounded-xl p-6 sm:p-8 border border-border shadow-sm">
            <h2 className="text-lg font-semibold text-foreground mb-6 flex items-center gap-2">
              {t('Classroom.rosterTitle')}
              {approved.length > 0 && <Badge>{approved.length}</Badge>}
            </h2>

            {isLoading ? (
              <p className="text-sm text-muted-foreground">{t('Classroom.loading')}</p>
            ) : approved.length === 0 ? (
              <EmptyState icon={Users} title={t('Classroom.noStudents')} hint={t('Classroom.noStudentsHint')} />
            ) : (
              <div className="space-y-2">
                {approved.map(membership => {
                  const student = studentsByID[membership.studentID];
                  const isMenuOpen = openMenuID === membership._id;

                  return (
                    <div
                      key={membership._id}
                      className="relative flex items-center justify-between gap-3 p-3 bg-background border border-border rounded-lg"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold text-white shrink-0 ${colorForKey(
                            membership.studentID,
                            AVATAR_COLORS,
                          )}`}
                        >
                          {getInitials(student?.firstName, student?.lastName)}
                        </div>
                        <p className="font-medium text-foreground truncate">{studentLabel(membership.studentID)}</p>
                      </div>

                      <Button
                        variant="ghost"
                        size="icon-sm"
                        className="text-muted-foreground shrink-0"
                        aria-label={t('Classroom.studentOptions')}
                        onClick={() => setOpenMenuID(isMenuOpen ? null : membership._id)}
                      >
                        <MoreVertical className="h-4 w-4" />
                      </Button>

                      {isMenuOpen && (
                        <>
                          <div className="fixed inset-0 z-10" onClick={() => setOpenMenuID(null)} />
                          <div className="absolute right-3 top-11 z-20 w-44 bg-popover text-popover-foreground border border-border rounded-lg shadow-lg py-1 animate-in fade-in zoom-in-95 duration-100">
                            <button
                              type="button"
                              disabled
                              className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-left text-muted-foreground cursor-not-allowed"
                            >
                              {t('Classroom.editStudent')}
                              <Badge className="bg-muted text-muted-foreground normal-case">
                                {t('Profile.badges.soon')}
                              </Badge>
                            </button>
                            <button
                              type="button"
                              disabled
                              className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-left text-muted-foreground cursor-not-allowed"
                            >
                              {t('Classroom.removeStudent')}
                              <Badge className="bg-muted text-muted-foreground normal-case">
                                {t('Profile.badges.soon')}
                              </Badge>
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* Exams the class attended — full width, always shown last regardless of viewport. */}
          <section className="order-3 lg:col-span-3 bg-card rounded-xl p-6 sm:p-8 border border-border shadow-sm">
            <h2 className="text-lg font-semibold text-foreground mb-6 flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-brand-text" /> {t('ClassInsights.examListTitle')}
            </h2>

            {!isLoadingExams && exams.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                {(
                  [
                    { key: 'examCount', Icon: BookOpen, value: String(exams.length) },
                    { key: 'subjectCount', Icon: GraduationCap, value: String(subjectCount) },
                    {
                      key: 'averageGrade',
                      Icon: Check,
                      value: classAverageGrade != null ? classAverageGrade.toFixed(1) : '—',
                    },
                  ] as const
                ).map(({ key, Icon, value }) => (
                  <div key={key} className="flex items-center gap-3 p-4 bg-background border border-border rounded-lg">
                    <div className="w-10 h-10 bg-brand-surface rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon className="h-5 w-5 text-brand-text" />
                    </div>
                    <div>
                      <p className="text-xl font-semibold text-foreground">{value}</p>
                      <p className="text-sm text-muted-foreground">{t(`ClassInsights.${key}`)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {isLoadingExams ? (
              <p className="text-sm text-muted-foreground">{t('ClassInsights.loading')}</p>
            ) : exams.length === 0 ? (
              <p className="text-sm italic text-muted-foreground/70">{t('ClassInsights.noExams')}</p>
            ) : (
              <div className="space-y-2">
                {exams.map(exam => (
                  <button
                    key={exam.conversationId}
                    type="button"
                    onClick={() => exam.conversationId && onOpenExam(exam.conversationId)}
                    className="w-full flex items-center justify-between gap-3 p-4 bg-background border border-border rounded-lg text-left hover:bg-accent transition-colors"
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-foreground truncate">
                        {exam.title || t('ClassInsights.unnamedExam')}
                      </p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className="px-2 py-0.5 bg-brand-surface text-brand-text border border-brand-border rounded text-xs font-medium">
                          {subjectLabel(exam.subject)}
                        </span>
                        <span
                          className={`px-2 py-0.5 rounded text-xs font-medium ${
                            isGraded(exam.status) ? 'bg-green-50 text-green-700' : 'bg-muted text-muted-foreground'
                          }`}
                        >
                          {isGraded(exam.status) ? t('ClassInsights.statusGraded') : t('ClassInsights.statusInProgress')}
                        </span>
                        {exam.submissionCount > 0 && (
                          <span className="px-2 py-0.5 bg-amber-50 text-amber-700 rounded text-xs font-medium">
                            {exam.averageGrade != null
                              ? t('ClassInsights.averageGradeShort', { grade: exam.averageGrade.toFixed(1) })
                              : `${exam.averagePercentage.toFixed(0)}%`}
                          </span>
                        )}
                        {exam.createdAt && (
                          <span className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Clock className="h-3 w-3" /> {formatDate(exam.createdAt)}
                          </span>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                  </button>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>

      <Dialog open={isQrOpen} onOpenChange={setIsQrOpen}>
        <DialogContent className="sm:max-w-xs bg-card text-card-foreground border-border">
          <DialogHeader>
            <DialogTitle>{current.name}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-2">
            <div className="bg-white p-4 rounded-lg">
              <QRCode value={inviteLink} size={192} />
            </div>
            <div className="text-center">
              <p className="text-xs text-muted-foreground">{t('Classroom.qrCodeDialogTitle')}</p>
              <p className="text-2xl font-mono tracking-widest text-foreground">{current.code}</p>
              <p className="text-xs text-muted-foreground mt-1">{t('Classroom.qrCodeDialogHint')}</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
